import { submitActionAfterOTPConfirmation } from "@js/apps/common/forms/fields/otp/otp-auth-modal";
import { useUser } from "@js/apps/common/hooks/use-user";
import { openOTPDisabledWarningModal } from "@js/apps/settings/components/otp-auth";
import { ModalConfirm, ModalInstance } from "@js/components/modal";
import type { WithdrawalMethodType } from "@js/types/withdrawals";

import {
  deleteWithdrawalMethod,
  setAsDefaultWithdrawalMethod as setAsDefaultWithdrawal,
} from "../actions";
import { useGetWithdrawalMethodsQuery } from "../api/api";
import { openAddRecipientsModal } from "../components/add-recipients";
import { handleResponseError } from "../utils";

import { useOpenBankingFromNonSupportedLocationModal } from "./use-open-banking-from-non-supported-location-modal-provider";

export const useWithdrawalMethods = () => {
  const user = useUser();
  const {
    data: withdrawalMethods = [],
    isLoading: fetchingWithdrawalMethods,
    refetch: refetchWithdrawalMethods,
  } = useGetWithdrawalMethodsQuery();

  useOpenBankingFromNonSupportedLocationModal({
    fetchingWithdrawalMethods,
    withdrawalMethods,
  });

  const onWithdrawalMethodDelete = (withdrawalMethod: WithdrawalMethodType) => {
    ModalInstance.open({
      children: (
        <ModalConfirm
          onCancel={ModalInstance.close}
          onConfirm={() => {
            ModalInstance.close();
            submitActionAfterOTPConfirmation({
              onSubmit: (values) => {
                return deleteWithdrawalMethod(withdrawalMethod.id, values)
                  .then(() => refetchWithdrawalMethods())
                  .catch((error) => {
                    handleResponseError(
                      error,
                      "Failed to delete banking information.",
                    );
                  });
              },
            });
          }}
        >
          Are you sure you want to delete this banking information?
        </ModalConfirm>
      ),
    });
  };

  const setAsDefaultWithdrawalMethod = (
    withdrawalMethod: WithdrawalMethodType,
  ) => {
    if (!user?.is_otp_enabled && !user?.is_passkey_enabled) {
      return openOTPDisabledWarningModal();
    }

    submitActionAfterOTPConfirmation({
      onSubmit: async (values) => {
        return setAsDefaultWithdrawal(withdrawalMethod.id, values)
          .then(() => refetchWithdrawalMethods())
          .catch((error) => {
            handleResponseError(
              error,
              "Failed to set banking information as default.",
            );
          });
      },
    });
  };

  const onAddRecipientsClick = (withdrawalMethod: WithdrawalMethodType) => {
    if (!user?.is_otp_enabled && !user?.is_passkey_enabled) {
      return openOTPDisabledWarningModal();
    }

    openAddRecipientsModal({ handledMethod: withdrawalMethod });
  };

  return {
    fetchingWithdrawalMethods,
    onAddRecipientsClick,
    setAsDefaultWithdrawalMethod,
    onWithdrawalMethodDelete,
    withdrawalMethods,
  };
};
